/** Branded ID parse functions -- the ONLY place `as XxxId` casts should appear. Validation is minimal (non-empty) because the value is compile-time safety; the throw is a plain `Error` to satisfy the utils purity floor. */
import type {
	AiSearchInstanceId,
	AiSearchItemId,
	BancAccountId,
	BidId,
	ConversationId,
	FlowStepId,
	GroupId,
	LedgerId,
	LedgerTransactionId,
	MarketMakerId,
	PropertyId,
	QuestionAnswerId,
	RefundId,
	SessionId,
	TraceId,
	TransactionId,
	UserId,
} from '@federa/types/primitives-identifiers';

/** Validate a raw string and cast to a branded ID type. Throws if empty. */
function parseBrandedId<T extends string>(raw: string, typeName: string): T {
	if (!raw) {
		throw new Error(`${typeName} cannot be empty`);
	}
	return raw as T;
}

/**
 * The per-brand parser FACTORY. Every export below is one call to it, because the alternative -- one hand-written
 * three-line wrapper per brand -- is the same body spelled eighteen times, and the length it accumulated is what put
 * this leaf over its cap. The parser's own contract is unchanged: a non-empty check, then the single sanctioned
 * `as <Brand>` cast, both living once in `parseBrandedId`.
 */
function brandedIdParser<T extends string>(typeName: string): (raw: string) => T {
	return (raw: string): T => parseBrandedId<T>(raw, typeName);
}

/** Parse and validate a user ID. Throws if empty. */
export const parseUserId = brandedIdParser<UserId>('UserId');

/** A user ID read off the wire where it is OPTIONAL: parsed when present, `undefined` for an absent or empty one. */
export function parseUserIdOrUndefined(raw: string | null | undefined): UserId | undefined {
	return raw ? parseUserId(raw) : undefined;
}

/** A user ID held where absence is `null` (a signed-out viewer, an unbound socket): parsed when present, `null` for an
 *  absent or empty one -- the guard every such reader spelled so the parse cannot throw while nobody is signed in. */
export function parseUserIdOrNull(raw: string | null | undefined): UserId | null {
	return raw ? parseUserId(raw) : null;
}

/** Parse and validate a PropertyDO bid ID. Throws if empty. */
export const parseBidId = brandedIdParser<BidId>('BidId');

/** Parse and validate a property ID. Throws if empty. */
export const parsePropertyId = brandedIdParser<PropertyId>('PropertyId');

/** A property ID held where absence is `null`: parsed when present, `null` for an absent or empty one. */
export function parsePropertyIdOrNull(raw: string | null | undefined): PropertyId | null {
	return raw ? parsePropertyId(raw) : null;
}

/** Parse and validate a session ID. Throws if empty. */
export const parseSessionId = brandedIdParser<SessionId>('SessionId');

/** Parse and validate a conversation ID. Throws if empty. */
export const parseConversationId = brandedIdParser<ConversationId>('ConversationId');

/** Parse and validate a ledger row ID. Throws if empty. */
export const parseLedgerId = brandedIdParser<LedgerId>('LedgerId');

/** Parse and validate a core-book ledger transaction ID (`transaction_ref`). Throws if empty. */
export const parseLedgerTransactionId = brandedIdParser<LedgerTransactionId>('LedgerTransactionId');

/** Parse and validate a PROVIDER-minted refund ID. Throws if empty. */
export const parseRefundId = brandedIdParser<RefundId>('RefundId');

/** Parse and validate a market-maker ID. Throws if empty. */
export const parseMarketMakerId = brandedIdParser<MarketMakerId>('MarketMakerId');

/** Parse and validate a flow-graph node ID. Throws if empty. */
export const parseFlowStepId = brandedIdParser<FlowStepId>('FlowStepId');

/** Parse and validate a distributed-trace correlation ID. Throws if empty. Brand the OUTPUT of
 * `resolveTelemetryIdentity` (`@federa/const/telemetry-identity`) here -- that leaf sits on the const floor and cannot
 * reach this module, so the SSOT mints a plain string and the branding happens at the consuming boundary. */
export const parseTraceId = brandedIdParser<TraceId>('TraceId');

/** Parse and validate a `home_details` transaction ID. Throws if empty. */
export const parseTransactionId = brandedIdParser<TransactionId>('TransactionId');

/** Parse and validate a `groups` row ID. Throws if empty. */
export const parseGroupId = brandedIdParser<GroupId>('GroupId');

/** Parse and validate a `questions_answers` row ID (the knowledgebase entry id). Throws if empty. */
export const parseQuestionAnswerId = brandedIdParser<QuestionAnswerId>('QuestionAnswerId');

/** Parse and validate an AI Search instance ID. Throws if empty. */
export const parseAiSearchInstanceId = brandedIdParser<AiSearchInstanceId>('AiSearchInstanceId');

/** Parse and validate an AI Search item ID. Throws if empty. */
export const parseAiSearchItemId = brandedIdParser<AiSearchItemId>('AiSearchItemId');

/** Parse and validate a banc-account (`banc_accounts` row) ID. Throws if empty. */
export const parseBancAccountId = brandedIdParser<BancAccountId>('BancAccountId');
